import { useMemo, useState } from 'react';
import { Button, Tooltip } from 'components/ui';
import {
  AtlasGqlTask,
  AtlasGqlInspectionTask,
  AtlasGqlWorkContainerTypeOption,
  AtlasGqlAssetType,
  AtlasGqlInspectionType,
} from 'types/atlas-graphql';
import {
  InitialStep,
  Steps,
  UploadInspectionDataModal,
  UploadTypes,
} from 'horizon/routes/Inspections/InspectionsUploadManager/UploadInspectionDataModal';

type UploadInspectionsToWorkOrderProps = {
  allTasks: AtlasGqlTask[];
  selected: AtlasGqlTask[];
};

export const UploadInspectionsToWorkOrder: React.FC<UploadInspectionsToWorkOrderProps> = ({
  allTasks,
  selected,
}) => {
  const [inspectionUploadModalOpen, setInspectionUploadModalOpen] = useState<boolean>(false);

  const {
    disabled,
    tooltip,
    initialStep,
    initialInspectionType,
    existingInspectionIds = [],
    inspectionTasks = [],
  } = useMemo(() => {
    // disable button for when there are no inspection tasks
    if (allTasks.every(task => task.type.type !== AtlasGqlWorkContainerTypeOption.InspectionTask)) {
      return {
        disabled: true,
        tooltip: 'There are no Inspection Tasks on this Work Order',
        initialStep: Steps.SummaryUpload,
      };
    }

    // disable button if selected tasks include non-inspection tasks
    if (selected.some(({ type }) => type.type !== AtlasGqlWorkContainerTypeOption.InspectionTask)) {
      return {
        disabled: true,
        tooltip: 'Cannot upload Inspections for Non-Inspection Tasks',
        initialStep: Steps.SummaryUpload,
      };
    }

    const tasks = selected.length ? selected : allTasks;
    const _inspectionTasks = tasks.filter(
      task => task.type.type === AtlasGqlWorkContainerTypeOption.InspectionTask
    ) as AtlasGqlInspectionTask[];
    const idsOfTasksWithoutInspections = _inspectionTasks
      .filter(({ inspection }) => !inspection)
      .map(({ id }) => id);
    const existingInspectionIds = _inspectionTasks
      .map(({ inspection }) => inspection?.id)
      .filter((inspectionId): inspectionId is string => !!inspectionId);
    const inspectionTasks = _inspectionTasks.map(task => {
      const { id, inspection, asset } = task;
      return {
        taskId: id,
        inspectionId: inspection?.id,
        inspectionType: inspection?.type ?? undefined,
        assetType: asset.assetType ?? undefined,
      };
    });

    // disable button if selected tasks are on different asset types
    const taskAssetTypes = _inspectionTasks
      .map(({ asset }) => asset.assetType)
      .filter((type): type is AtlasGqlAssetType => !!type);
    if (!taskAssetTypes.every(type => taskAssetTypes[0].name === type.name)) {
      return {
        disabled: true,
        tooltip: 'Cannot upload Inspections of different Asset Types',
        initialStep: Steps.SummaryUpload,
      };
    }

    // disable button if selected tasks have existing inspections with different inspection types
    const taskInspectionTypes = _inspectionTasks
      .map(({ inspection }) => inspection?.type)
      .filter((type): type is AtlasGqlInspectionType => !!type);
    if (!taskInspectionTypes.every(type => taskInspectionTypes[0].name === type.name)) {
      return {
        disabled: true,
        tooltip: 'Cannot upload Inspections of different Inspection Types',
        initialStep: Steps.SummaryUpload,
      };
    }

    let initialStep = Steps.SummaryUpload;
    if (!idsOfTasksWithoutInspections.length) {
      initialStep = Steps.DetailsUpload;
    }

    return {
      disabled: !idsOfTasksWithoutInspections.length && !inspectionTasks.length,
      existingInspectionIds,
      inspectionTasks,
      initialStep,
      initialInspectionType: taskInspectionTypes.length ? taskInspectionTypes[0] : undefined,
    };
  }, [selected, allTasks]);

  return (
    <>
      <Tooltip title={disabled ? tooltip : ''}>
        <div>
          <Button
            data-testid="wo-inspection-upload-button"
            onClick={() => setInspectionUploadModalOpen(true)}
            disabled={disabled}
          >
            Upload Inspections
          </Button>
        </div>
      </Tooltip>
      <UploadInspectionDataModal
        open={inspectionUploadModalOpen}
        onClose={() => setInspectionUploadModalOpen(false)}
        lockedUploadType={UploadTypes.Multiple}
        initialStep={initialStep as InitialStep}
        initialInspectionType={initialInspectionType}
        existingInspectionIds={existingInspectionIds}
        inspectionTasks={inspectionTasks}
        requireTaskId={true}
      />
    </>
  );
};
